<script setup>
import { computed, ref, reactive, provide, toRef, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useNotification } from '@kyvg/vue3-notification'
import _ from 'lodash'
import { showModal, hideModal } from '@/Utils/modalBus'
import StreamService from '@/services/StreamService'
import DestinationService from '@/services/DestinationService'
import utils from '@/utils'
import { track, EVENTS } from '@/lib/tracking'
import { coloredPlatformIcon } from '@/Utils/platformIcon'
import { pushStreamFailOverEnabled, getLinkedSourceTooltip } from '@/Utils/platformStatus'

import { usePlatformList } from '@/composables/usePlatformList'
import { usePlatformToggle } from '@/composables/usePlatformToggle'
import { useDestinationGroups } from '@/composables/useDestinationGroups'
import { usePlatformOauth } from '@/composables/usePlatformOauth'
import { usePlatformMetadata } from '@/composables/usePlatformMetadata'
import { useGuestDestinations } from '@/composables/useGuestDestinations'
import { PLATFORM_LIST_KEY } from './PlatformList/platformListContext'

import Spinner from '@/components/ui/Spinner.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import AddPlatformModal from '@/components/modals/AddPlatformModal.vue'
import DemoVideoModal from '@/components/modals/DemoVideoModal.vue'
import AddCastrStreamModal from '@/components/modals/AddCastrStreamModal.vue'
import AlertModal from '@/components/modals/AlertModal.vue'
import EditPlatformModal from '@/components/modals/ConfigurePlatformModal.vue'
import EditDestinationModal from '@/components/modals/EditDestinationModal.vue'
import EditYoutubeEventModal from '@/components/modals/EditYoutubeEventModal.vue'
import SubSecondRecommendedSettings from '@/components/SubSecondStream/SubSecondRecommendedSettings.vue'
import PLgroupDSidebar from '@/components/modals/PLgroupDSidebar.vue'
import GuestDestinationPopup from '@/components/modals/GuestDestinationPopup.vue'
import GuestShareLink from '@/components/modals/GuestShareLink.vue'
import ManageAccessList from '@/components/modals/ManageAccessList.vue'
import PlatformListToolbar from './PlatformList/PlatformListToolbar.vue'
import PlatformListEmpty from './PlatformList/PlatformListEmpty.vue'
import PlatformSection from './PlatformList/PlatformSection.vue'
import CastrStreamRow from './PlatformList/CastrStreamRow.vue'
import DestinationRow from './PlatformList/DestinationRow.vue'
import DestinationGroupCard from './PlatformList/DestinationGroupCard.vue'
import MoveToGroupModal from './PlatformList/MoveToGroupModal.vue'
import EditGroupModal from './PlatformList/EditGroupModal.vue'
import DeleteGroupModal from './PlatformList/DeleteGroupModal.vue'

const props = defineProps({
	stream: {
		type: Object,
		required: true,
		validator(v) {
			return Object.prototype.hasOwnProperty.call(v, '_id')
		},
	},
	mediaPulse: {
		type: Object,
		default() { return {} },
	},
})

const emit = defineEmits(['platform-list', 'refresh-stream'])

const store = useStore()
const { notify } = useNotification()

const stream = toRef(props, 'stream')
const mediaPulse = toRef(props, 'mediaPulse')

const streamPlatforms = ref([])
const regions = ref([])
const schedulerConfig = ref({})
const platformUsage = ref({})
const addedPlatformIds = ref([])
const selectedPlatform = ref({})
const selectedDestinationEdit = ref(null)
const alertMessage = ref('')
const deleteBlock = ref(false)
const platformDeleted = ref(false)
const platformAdded = ref(false)

const sidebarAddStream = ref(null)
const modalSelectStreamModal = ref(null)

const loadingStatus = computed(() => store.getters['Ui/loadingStatus'])
const user = computed(() => store.state.User)
const activeTeam = computed(() => store.state.User.activatedTeam)
const userEmail = computed(() => store.state.User.email)
const memberPermission = computed(() => activeTeam.value && store.state.User.memberPermission)

const cannotTogglePlatforms = computed(() => {
	if (!activeTeam.value || !memberPermission.value) return false
	const permissionMap = {
		live: memberPermission.value.liveStreamPlatformToggle,
		restream: memberPermission.value.recordedStreamPlatformToggle,
		scheduled: memberPermission.value.preRecordedStreamPlatformToggle,
		subsecond: memberPermission.value.subSecondStreamManage,
	}
	return !permissionMap[stream.value?.type]
})

const guestDestinationEnabled = computed(() => {
	const liveSub = _.find(store.state.User.addonSub, { category: 'live', enabled: true })
	if (!liveSub) return false
	return !!liveSub?.package?.definition?.guestDestinations
})

function onPlatformList(list) {
	emit('platform-list', list)
}

let reloadPlatforms = async () => {}
const oauth = usePlatformOauth({
	user,
	stream,
	onReload: () => reloadPlatforms(),
})

const guests = useGuestDestinations(stream)

const metadata = usePlatformMetadata({ streamPlatforms })

const groupDeps = {}
const groupsApi = useDestinationGroups({
	stream,
	streamPlatforms,
	deps: groupDeps,
})

const toggleApi = usePlatformToggle({
	stream,
	mediaPulse,
	streamPlatforms,
	groups: groupsApi.groups,
	groupToggleStateMap: groupsApi.groupToggleStateMap,
	groupToggleProcessing: groupsApi.groupToggleProcessing,
	schedulerConfig,
	computeGroupToggleStateForGroup: groupsApi.computeGroupToggleStateForGroup,
	onPlatformList,
})

groupDeps.setPlatformsEnabled = toggleApi.setPlatformsEnabled
groupDeps.computeGroupToggleState = toggleApi.computeGroupToggleState

const list = usePlatformList({
	stream,
	mediaPulse,
	streamPlatforms,
	regions,
	schedulerConfig,
	oauth,
	platformToggleProcessing: toggleApi.platformToggleProcessing,
	computeGroupToggleState: toggleApi.computeGroupToggleState,
	onPlatformList,
})
reloadPlatforms = list.reloadPlatforms

const {
	groups,
	groupToggleStateMap,
	groupName,
	selectedOptions,
	currentGroupName,
	showEditGroupModal,
	showDeleteGroupModal,
	showMoveToGroupModal,
	editedGroupName,
	groupToDelete,
	targetGroupName,
	getSelectedGroup,
	computeGroupToggleStateForGroup,
	toggleGroupedPlatforms,
	toggleGroup,
	isGroupCollapsed,
	requestGroupEdit,
	confirmGroupEdit,
	deleteGroup,
	cancelDeleteGroup,
	confirmDeleteGroup,
	requestPlatformMoveToGroup,
	confirmMoveToGroup,
	onCreateClicked,
	handleCancel,
	openAddPlatformForGroup,
	addPlatformToGroup,
} = groupsApi

const {
	groupToggleState,
	appNotificationMsg,
	toggleGroupStatus,
	onToggleOffWarningConfirmed,
	onTogglePlatformStatus,
} = toggleApi

const {
	configurablePlatform,
	configurableField,
	hasMetaProp,
	onMetaPropChange,
	saveAllMeta,
	requestMetaToAllPlatforms,
	onAllPlatformsUpdateConfirm,
	onChangeGameName,
} = metadata

const platformCastrFiltered = computed(() =>
	streamPlatforms.value.filter((p) =>
		p.server?.toLowerCase().includes('castr.io') && (p.streamObject || p.transcoderObject)
	)
)

const platformRegularFiltered = computed(() => {
	const groupedPlatforms = []
	groups.value.forEach((g) => groupedPlatforms.push(...g.selected))
	return streamPlatforms.value.filter((p) =>
		!p.streamObject && !p.transcoderObject && !groupedPlatforms.includes(p._id)
	)
})

const platformCastrLiveFiltered = computed(() =>
	platformCastrFiltered.value.filter((platform) =>
		platform.streamObject && (!platform.transcoderObject || platform.streamObject._id !== platform?.transcoderObject?._id)
	)
)

const platformCastrTranscoderFiltered = computed(() =>
	platformCastrFiltered.value.filter((platform) =>
		platform.transcoderObject && (!platform.streamObject || platform.streamObject._id === platform?.transcoderObject?._id)
	)
)

const hasOtherDestinationSections = computed(() =>
	platformCastrLiveFiltered.value.length > 0
	|| platformCastrTranscoderFiltered.value.length > 0
	|| groups.value.length > 0
)

const deleteConfirmBody = computed(() => {
	const p = configurablePlatform.value
	const name = p && (p.editorName || p.castrName || p.name)
	return name
		? `“${name}” will be removed from this stream.`
		: 'This destination will be removed from this stream.'
})

async function fetchPlatformUsage() {
	const usage = await store.dispatch('PlatformUsage/getPlatformUsage', {
		type: stream.value.type,
		force: true,
	})
	// Store action returns the Vuex object; clone so GuestDestinationPopup can
	// bump usedDestination without a "do not mutate the store" error.
	if (usage) platformUsage.value = _.cloneDeep(usage)
}

async function destinationsChanged() {
	await list.fetchStreamPlatformList()
	emit('refresh-stream')
	store.commit('PlatformUsage/CLEAR_USAGE', stream.value.type)
	await fetchPlatformUsage()
}

async function onNewPlatform(platform, groupNameValue) {
	addPlatformToGroup(platform, groupNameValue)
	if (!addedPlatformIds.value.includes(platform._id)) {
		addedPlatformIds.value.push(platform._id)
	}
	platformAdded.value = !platformAdded.value
	store.commit('PlatformUsage/CLEAR_USAGE', stream.value.type)
	await list.fetchStreamPlatformList()
	await fetchPlatformUsage()
	emit('refresh-stream', () => {
		setTimeout(() => {
			list.normalizePlatformList()
		}, 200)
	})
}

async function onPlatformNameChange(newName) {
	if (selectedPlatform.value.name === newName) return
	try {
		await StreamService.setStreamPlatformName(
			stream.value._id,
			selectedPlatform.value._id,
			newName
		)
		selectedPlatform.value = {}
	} catch (err) {
		selectedPlatform.value = {}
		notify({
			group: 'error',
			title: "Couldn't change platform name",
			text: err.message,
		})
	}
}

function onPlatformUpdated(targetPlatform, updates) {
	const platIndex = _.findIndex(streamPlatforms.value, { _id: targetPlatform._id })
	if (platIndex > -1) {
		const updatedPlatform = _.assign({}, configurablePlatform.value, updates)
		streamPlatforms.value = utils.updateArrayItem(streamPlatforms.value, updatedPlatform, platIndex)
		onPlatformList(streamPlatforms.value)
		list.checkPlatformConnectStatus(updatedPlatform)
	}
}

function requestPlatformModify(platform) {
	if (platform.reusableDestination) {
		selectedDestinationEdit.value = platform
		showModal('edit-destination-modal')
		return
	}
	if (platform.linkedServiceCreds && platform.template === 'youtube events') {
		configurablePlatform.value = platform
		showModal('edit-youtube-event-modal')
		return
	}
	if (platform.linkedServiceCreds) {
		alertMessage.value = `Platform is connected to ${platform.template} account and cannot be edited but can be toggled or deleted`
		showModal('platform-is-linked')
		return
	}
	configurablePlatform.value = platform
	showModal('modal-configure-platform')
}

function onInlineRemove(platform) {
	configurablePlatform.value = platform
	onPlatformDeleteConfirm()
}

async function onPlatformDeleteConfirm() {
	const platform = configurablePlatform.value
	platform.removingProcessing = true
	deleteBlock.value = false
	if (platform.enabled) {
		hideModal('platform-delete-confirm')
		notify({
			group: 'error',
			title: 'Cannot delete enabled platform',
			text: 'Please disable the platform before deleting it',
		})
		platform.removingProcessing = false
		deleteBlock.value = false
		return
	}
	try {
		if (platform.reusableDestination) {
			await DestinationService.removeDestinationFromStream(platform._id, stream.value._id)
			await store.dispatch('Destination/getDestinations', true)
		} else {
			await StreamService.deleteStreamPlatform(stream.value._id, platform._id)
		}
		destinationsChanged()
		const platforms = streamPlatforms.value
		const index = platforms.indexOf(platform)
		streamPlatforms.value = _.concat(platforms.slice(0, index), platforms.slice(index + 1))
		onPlatformList(streamPlatforms.value)
		emit('refresh-stream')
		platformDeleted.value = !platformDeleted.value
		toggleApi.computeGroupToggleState()
		track(EVENTS.DESTINATION_REMOVED, {
			streamType: stream.value.type,
			destinationTemplate: platform.template,
		})
		deleteBlock.value = false
	} catch (err) {
		notify({
			group: 'error',
			title: "Couldn't delete publish platform",
			text: err.message,
		})
		platform.removingProcessing = false
		deleteBlock.value = false
	}
}

async function onRemoveGuestDestinations(removedEmail) {
	guests.clearGuestSet()
	await modalSelectStreamModal.value?.onRemovedGuest(removedEmail)
}

function showCreateStreamSidebar() {
	sidebarAddStream.value?.toggleSidebar()
}

function onClickGuestPopup() {
	showModal('select-guest-destinations-modal')
}

function onSelectedOptionsUpdate(val) {
	selectedOptions.value = val
}

function navigatePaymentsPage() {
	window.location.href = '/app/subscribe'
}

function setSelectedPlatform(platform) {
	selectedPlatform.value = platform
}

const ctx = reactive({
	streamType: computed(() => stream.value.type),
	mediaPulse,
	regions,
	cannotTogglePlatforms,
	deleteBlock,
	userEmail,
	hasGroups: computed(() => groups.value.length > 0),
	facebookOauthState: oauth.facebookOauthState,
	youtubeOauthState: oauth.youtubeOauthState,
	groupToggleStateMap,
	getFacebookUrl: oauth.getFacebookUrl,
	getLinkedSourceTooltip,
	pushStreamFailOverEnabled,
	coloredIcon: coloredPlatformIcon,
	isGuestDestinationRemoved: guests.isGuestDestinationRemoved,
	onTogglePlatformStatus,
	onInlineRemove,
	requestPlatformModify,
	requestPlatformMoveToGroup,
	onPlatformNameChange,
	setSelectedPlatform,
	hasMetaProp,
	onMetaPropChange,
	saveAllMeta,
	requestMetaToAllPlatforms,
	onChangeGameName,
	getSelectedGroup,
	toggleGroup,
	isGroupCollapsed,
	toggleGroupedPlatforms,
	requestGroupEdit,
	deleteGroup,
	openAddPlatformForGroup,
})

provide(PLATFORM_LIST_KEY, ctx)

onMounted(async () => {
	await list.bootstrap({
		onNotScheduled: () => guests.fetchGuestDestinations(),
		afterFirstNormalize: async () => {
			try {
				if (stream.value.metadata && stream.value.metadata.destinationGroups) {
					groups.value = _.cloneDeep(stream.value.metadata.destinationGroups) || []
					groups.value.forEach((group) => {
						computeGroupToggleStateForGroup(group.name)
					})
				}
			} catch (e) {
				console.log(e)
			}
		},
	})
	if (groups.value.length) {
		groups.value.forEach((group) => {
			computeGroupToggleStateForGroup(group.name)
		})
	}
	fetchPlatformUsage()
})

onUnmounted(() => {
	list.teardown()
	onChangeGameName.cancel?.()
})
</script>

<template>
	<div class="platform-list-section min-w-0 w-full max-w-full" :class="stream.type === 'scheduled' ? 'is-scheduled-platforms' : ''">
		<div v-if="loadingStatus" class="flex justify-center mt-10" aria-busy="true">
			<Spinner classes="text-dark-8" spinner-color="var(--c-dark-8)" spinner-size="22px" />
		</div>
		<div v-else-if="!streamPlatforms.length && !groups.length" class="placeholder">
			<PlatformListToolbar
				:stream-type="stream.type"
				:platform-usage="platformUsage"
				:guest-destination-enabled="guestDestinationEnabled"
				:cannot-toggle-platforms="cannotTogglePlatforms"
				:user-email="userEmail"
				@new-group="showCreateStreamSidebar"
				@invite-guest="onClickGuestPopup"
			/>
			<PlatformListEmpty :stream-type="stream.type" />
		</div>
		<div v-else class="min-w-0 w-full max-w-full">
			<PlatformListToolbar
				:stream-type="stream.type"
				:platform-usage="platformUsage"
				show-toggle-all
				:guest-destination-enabled="guestDestinationEnabled"
				:cannot-toggle-platforms="cannotTogglePlatforms"
				:user-email="userEmail"
				v-model:group-toggle-state="groupToggleState"
				@toggle-all="toggleGroupStatus"
				@new-group="showCreateStreamSidebar"
				@invite-guest="onClickGuestPopup"
			/>
			<SubSecondRecommendedSettings v-if="stream.type === 'subsecond'" class="mb-4" />

			<PlatformSection
				v-if="platformCastrLiveFiltered.length > 0"
				title="Castr Streams"
				:scheduled="stream.type === 'scheduled'"
			>
				<CastrStreamRow
					v-for="platform in platformCastrLiveFiltered"
					:key="platform._id"
					:platform="platform"
					kind="stream"
				/>
			</PlatformSection>

			<PlatformSection
				v-if="platformCastrTranscoderFiltered.length > 0"
				title="Transcoders"
				:scheduled="stream.type === 'scheduled'"
			>
				<CastrStreamRow
					v-for="platform in platformCastrTranscoderFiltered"
					:key="platform._id"
					:platform="platform"
					kind="transcoder"
				/>
			</PlatformSection>

			<PlatformSection v-if="groups.length > 0" title="Groups" destinations :boxed="false">
				<DestinationGroupCard v-for="group in groups" :key="group.name" :group="group" />
			</PlatformSection>

			<h3
				v-if="platformRegularFiltered.length > 0 && hasOtherDestinationSections && stream.type !== 'scheduled'"
				class="text-sm font-medium text-surface-8 mt-5 mb-2"
			>
				Destinations
			</h3>
			<div
				v-if="platformRegularFiltered.length > 0"
				class="platform-list destinations-platform-list flex flex-col min-w-0 w-full bg-surface-1 rounded-xl p-2 gap-y-0.5 md:gap-y-1"
				:class="(hasOtherDestinationSections && stream.type !== 'scheduled') ? '' : 'mt-5'"
			>
				<DestinationRow
					v-for="(platform, index) in platformRegularFiltered"
					:key="platform._id"
					:platform="platform"
					:index="index"
				/>
			</div>
		</div>

		<MoveToGroupModal
			v-model:is-visible="showMoveToGroupModal"
			v-model:target-group-name="targetGroupName"
			:groups="groups"
			@confirm="confirmMoveToGroup"
		/>
		<DeleteGroupModal
			v-model:is-visible="showDeleteGroupModal"
			:group-name="groupToDelete"
			@confirm="confirmDeleteGroup"
			@cancel="cancelDeleteGroup"
		/>
		<EditGroupModal
			v-model:is-visible="showEditGroupModal"
			v-model:edited-group-name="editedGroupName"
			@confirm="confirmGroupEdit"
		/>

		<ManageAccessList
			ref="modalManageAccessList"
			:streamId="stream._id"
			@remove-guest-destinations="onRemoveGuestDestinations"
		/>
		<GuestShareLink ref="modalSelectStreamLink" :streamId="stream._id" />
		<GuestDestinationPopup
			ref="modalSelectStreamModal"
			:stream="stream"
			:platformUsage="platformUsage"
			:disabled-platform-ids="addedPlatformIds"
			@destinations-changed="destinationsChanged"
			@platform-saved="onNewPlatform"
			@refresh-guest-destinations="guests.fetchGuestDestinations"
		/>
		<PLgroupDSidebar
			ref="sidebarAddStream"
			:streamPlatforms="platformRegularFiltered"
			v-model:groupName="groupName"
			:selectedOptions="selectedOptions"
			@create-clicked="onCreateClicked"
			@cancel-clicked="handleCancel"
			@update:selectedOptions="onSelectedOptionsUpdate"
		/>

		<DemoVideoModal :videoid="stream.type === 'scheduled' ? 'GFvZ8ttEszBeIHGAPbDsx' : 'nlAJJ_mROavGyHPRs8ZZw'" />
		<AddPlatformModal
			:stream="stream"
			:selected-group="currentGroupName"
			:platform-deleted="platformDeleted"
			:platform-usage-data="platformUsage"
			@platform-saved="onNewPlatform"
			@destinations-changed="destinationsChanged"
		/>
		<EditDestinationModal :destination="selectedDestinationEdit" @updated="destinationsChanged" />
		<AddCastrStreamModal
			:stream="stream"
			:platform-castr-filtered="platformCastrFiltered"
			:platform-deleted="platformDeleted"
			:platform-added="platformAdded"
			:platform-usage-data="platformUsage"
			@platform-saved="onNewPlatform"
		/>
		<EditPlatformModal :stream="stream" :platform="configurablePlatform" @platform-updated="onPlatformUpdated" />
		<EditYoutubeEventModal :stream="stream" :platform="configurablePlatform" @platform-updated="onPlatformUpdated" />

		<ConfirmModal
			modal-id="platform-delete-confirm"
			modal-type="danger"
			modal-size="sm"
			message="Remove destination?"
			:body="deleteConfirmBody"
			ok-text="Remove"
			cancel-text="Cancel"
			@modal-confirm="onPlatformDeleteConfirm(configurablePlatform)"
		/>
		<ConfirmModal
			:message="appNotificationMsg"
			modal-id="modal-trial-publish-limit"
			modal-size="md"
			ok-text="Upgrade now"
			cancel-text="Ok fine"
			@modal-confirm="navigatePaymentsPage"
		/>
		<ConfirmModal
			message="Warning! Your Facebook stream is live"
			body="Toggling this off now will end your livestream on Facebook."
			modal-id="toggle-off-warning"
			modal-size="md"
			ok-text="Toggle off"
			cancel-text="Go back"
			@modal-confirm="onToggleOffWarningConfirmed"
		/>
		<ConfirmModal
			modal-id="all-platforms-update-confirm"
			message="Do you want to save for all platforms? This feature supports Facebook Page/Group, YouTube, and Twitch only."
			cancel-text="Cancel"
			ok-text="Save for All Platforms"
			@modal-confirm="onAllPlatformsUpdateConfirm(configurableField, configurablePlatform)"
		/>
		<AlertModal
			:message="alertMessage"
			modal-id="platform-is-linked"
			button-alignment="text-right"
			ok-text="Got it, thanks"
		/>
	</div>
</template>
